import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import { createCapacityRankingOption } from '../../../../utils/comparisonUtils'

/**
 * LocationRankingChart (Bare) - Chart and table only, no wrapper
 * For embedding in narrative HTML templates
 */
function LocationRankingChart({ data, title = 'Location Ranking' }) {
  if (!data || !data.rankings || data.rankings.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No sub-location comparison data available
      </div>
    )
  }

  const { rankings } = data
  const chartOption = createCapacityRankingOption(rankings, title)

  return (
    <div className="space-y-6">
      {/* Bar Chart */}
      <ReactECharts
        option={chartOption}
        style={{ height: '400px' }}
        opts={{ renderer: 'canvas' }}
        theme="ug-dashboard"
      />

      {/* Detailed Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rank
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Facilities
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Service Coverage
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Infrastructure
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Composite Score
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rankings.map((location, index) => (
              <tr
                key={index}
                className={
                  location.rank <= 3
                    ? 'bg-green-50'
                    : location.rank > rankings.length - 3
                      ? 'bg-red-50'
                      : ''
                }
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <span
                      className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        location.rank === 1
                          ? 'bg-yellow-400 text-yellow-900'
                          : location.rank === 2
                            ? 'bg-gray-300 text-gray-900'
                            : location.rank === 3
                              ? 'bg-orange-300 text-orange-900'
                              : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {location.rank}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {location.name}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                  {location.facilityCount}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <span
                    className={`text-sm font-medium ${
                      location.avgServiceCoverage >= 80
                        ? 'text-green-600'
                        : location.avgServiceCoverage >= 60
                          ? 'text-yellow-600'
                          : 'text-red-600'
                    }`}
                  >
                    {location.avgServiceCoverage}%
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <span
                    className={`text-sm font-medium ${
                      location.avgInfrastructure >= 80
                        ? 'text-green-600'
                        : location.avgInfrastructure >= 60
                          ? 'text-yellow-600'
                          : 'text-red-600'
                    }`}
                  >
                    {location.avgInfrastructure}%
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <span className="text-sm font-bold text-gray-900">
                    {location.compositeScore}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

LocationRankingChart.propTypes = {
  data: PropTypes.shape({
    rankings: PropTypes.arrayOf(
      PropTypes.shape({
        rank: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        facilityCount: PropTypes.number.isRequired,
        avgServiceCoverage: PropTypes.number.isRequired,
        avgInfrastructure: PropTypes.number.isRequired,
        compositeScore: PropTypes.number.isRequired,
      })
    ).isRequired,
  }).isRequired,
  title: PropTypes.string,
}

export default LocationRankingChart
